import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";
import { format } from "date-fns";
import { zhCN } from "date-fns/locale";
import { CalendarIcon, Check, ChevronDown, ListFilter, Minus, X } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  FILE_TYPE_OPTIONS,
  constrainDateRangeBound,
  constrainSizeRangeBound,
  isCalendarDayAfter,
  isCalendarDayBefore,
  isFileTypeSelected,
  isFileTypesSelectAllIndeterminate,
  parseFilterDate,
  toFilterDateIso,
  toggleFileTypeFilter,
  type DateRangeFilter,
  type FileTypeFacet,
  type MaterialsClientFilters,
  type SizeRangeFilter,
  type SizeUnit,
} from "@/lib/materials-filters";
import { cn } from "@/lib/utils";

type OperatorOption = { id: number; name: string };

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accountId: string;
  operators: OperatorOption[];
  /** 远程成员解析后的操作员名，供标签行展示 */
  onOperatorsResolved?: (operators: OperatorOption[]) => void;
  clientFilters: MaterialsClientFilters;
  onClientFiltersChange: (next: MaterialsClientFilters) => void;
  activeCount: number;
  /** 工具栏整行，面板宽度与水平定位对齐该元素 */
  toolbarRef: RefObject<HTMLElement | null>;
};

type MembershipRow = {
  user_id: number;
  disabled: boolean;
  user: {
    phone: string | null;
    display_label?: string;
  };
};

function FacetCheck({
  checked,
  indeterminate = false,
  label,
  onToggle,
}: {
  checked: boolean;
  indeterminate?: boolean;
  label: string;
  onToggle: () => void;
}) {
  const active = checked || indeterminate;
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={indeterminate ? "mixed" : checked}
      className={cn(
        "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted",
        active && "bg-muted/70",
      )}
      onClick={onToggle}
    >
      <span
        className={cn(
          "flex size-4 shrink-0 items-center justify-center rounded border border-border",
          active && "border-primary bg-primary text-primary-foreground",
        )}
        aria-hidden
      >
        {checked ? (
          <Check className="size-3" />
        ) : indeterminate ? (
          <Minus className="size-3" />
        ) : null}
      </span>
      <span className="truncate">{label}</span>
    </button>
  );
}

function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <p className="px-2 pb-1 text-[11px] font-medium tracking-wide text-muted-foreground uppercase">
      {children}
    </p>
  );
}

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

function formatBoundButton(value: string, emptyLabel: string): string {
  const d = parseFilterDate(value);
  if (!d) return emptyLabel;
  return format(d, "yyyy-MM-dd HH:mm", { locale: zhCN });
}

function localTimeValue(date: Date | undefined, fallback: string): string {
  if (!date) return fallback;
  return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

function combineLocalDateTime(
  day: Date,
  hours: number,
  minutes: number,
): Date {
  const next = new Date(day);
  next.setHours(hours, minutes, 0, 0);
  return next;
}

/** 起止时间：Calendar + 时刻输入，存 ISO；日历禁用越界日，变更时钳制 start≤end */
function DateTimeBoundField({
  ariaLabel,
  emptyLabel,
  value,
  onChange,
  defaultTime,
  minDate,
  maxDate,
}: {
  ariaLabel: string;
  emptyLabel: string;
  value: string;
  onChange: (iso: string) => void;
  /** 首次选日时的默认时分（起 00:00 / 止 23:59） */
  defaultTime: { hours: number; minutes: number };
  /** 不可早于（结束侧用） */
  minDate?: Date;
  /** 不可晚于（起始侧用） */
  maxDate?: Date;
}) {
  const [open, setOpen] = useState(false);
  const selected = parseFilterDate(value);
  const timeStr = localTimeValue(
    selected,
    `${pad2(defaultTime.hours)}:${pad2(defaultTime.minutes)}`,
  );

  function applyDay(day: Date | undefined) {
    if (!day) {
      onChange("");
      return;
    }
    const hours = selected ? selected.getHours() : defaultTime.hours;
    const minutes = selected ? selected.getMinutes() : defaultTime.minutes;
    onChange(toFilterDateIso(combineLocalDateTime(day, hours, minutes)));
  }

  function applyTime(hhmm: string) {
    const [hRaw, mRaw] = hhmm.split(":");
    const hours = Number(hRaw);
    const minutes = Number(mRaw);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return;
    const day = selected ?? new Date();
    onChange(toFilterDateIso(combineLocalDateTime(day, hours, minutes)));
  }

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn(
            "h-8 w-full justify-start gap-1.5 px-2 text-left text-xs font-normal",
            !selected && "text-muted-foreground",
          )}
          aria-label={ariaLabel}
        >
          <CalendarIcon className="size-3.5 shrink-0 opacity-70" />
          <span className="truncate">{formatBoundButton(value, emptyLabel)}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-3" align="start" sideOffset={4}>
        <Calendar
          mode="single"
          locale={zhCN}
          selected={selected}
          onSelect={applyDay}
          disabled={(date) => {
            if (maxDate && isCalendarDayAfter(date, maxDate)) return true;
            if (minDate && isCalendarDayBefore(date, minDate)) return true;
            return false;
          }}
          className="rounded-md"
        />
        <div className="mt-2 flex items-center gap-2 border-t border-border pt-2">
          <Label htmlFor={`${ariaLabel}-time`} className="shrink-0 text-xs text-muted-foreground">
            时间
          </Label>
          <Input
            id={`${ariaLabel}-time`}
            type="time"
            value={timeStr}
            onChange={(e) => applyTime(e.target.value)}
            className="h-8"
            aria-label={`${ariaLabel}时刻`}
          />
          {value ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-8 px-2"
              onClick={() => onChange("")}
            >
              清除
            </Button>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function DateRangeFields({
  label,
  value,
  onChange,
}: {
  label: string;
  value: DateRangeFilter;
  onChange: (next: DateRangeFilter) => void;
}) {
  const fromDate = parseFilterDate(value.from);
  const toDate = parseFilterDate(value.to);

  return (
    <div className="space-y-1.5 px-2">
      <SectionTitle>{label}</SectionTitle>
      <div className="grid grid-cols-1 gap-1.5 min-[420px]:grid-cols-2">
        <DateTimeBoundField
          ariaLabel={`${label}起始`}
          emptyLabel="起始时间"
          value={value.from}
          onChange={(from) => onChange(constrainDateRangeBound(value, "from", from))}
          defaultTime={{ hours: 0, minutes: 0 }}
          maxDate={toDate}
        />
        <DateTimeBoundField
          ariaLabel={`${label}结束`}
          emptyLabel="结束时间"
          value={value.to}
          onChange={(to) => onChange(constrainDateRangeBound(value, "to", to))}
          defaultTime={{ hours: 23, minutes: 59 }}
          minDate={fromDate}
        />
      </div>
    </div>
  );
}

function OperatorMultiSelect({
  accountId,
  knownOperators,
  selectedIds,
  onSelectedIdsChange,
  onOperatorsResolved,
}: {
  accountId: string;
  knownOperators: OperatorOption[];
  selectedIds: number[];
  onSelectedIdsChange: (ids: number[]) => void;
  onOperatorsResolved?: (operators: OperatorOption[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [members, setMembers] = useState<OperatorOption[]>([]);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    if (!accountId) {
      setLoadError("缺少账户上下文");
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    // 与成员页一致：X-Account-Id + /accounts/:id/memberships
    api
      .fetchForAccount<{ memberships: MembershipRow[] }>(
        accountId,
        `/accounts/${accountId}/memberships`,
      )
      .then(({ memberships }) => {
        if (cancelled) return;
        const opts = memberships
          .filter((m) => !m.disabled)
          .map((m) => ({
            id: m.user_id,
            name:
              m.user.display_label?.trim() ||
              m.user.phone?.trim() ||
              `用户 ${m.user_id}`,
          }))
          .sort((a, b) => a.name.localeCompare(b.name, "zh-CN"));
        setMembers(opts);
        onOperatorsResolved?.(opts);
      })
      .catch(() => {
        if (!cancelled) setLoadError("无法加载成员列表");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, accountId, onOperatorsResolved]);

  const options = useMemo(() => {
    const map = new Map<number, string>();
    for (const op of members) map.set(op.id, op.name);
    for (const op of knownOperators) {
      const existing = map.get(op.id);
      if (!existing || existing.startsWith("用户 ")) map.set(op.id, op.name);
    }
    return [...map.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, "zh-CN"));
  }, [members, knownOperators]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (op) => op.name.toLowerCase().includes(q) || String(op.id).includes(q),
    );
  }, [options, query]);

  const selectedOptions = useMemo(
    () =>
      selectedIds.map((id) => ({
        id,
        name: options.find((o) => o.id === id)?.name ?? `用户 ${id}`,
      })),
    [selectedIds, options],
  );

  function toggle(id: number) {
    const set = new Set(selectedIds);
    if (set.has(id)) set.delete(id);
    else set.add(id);
    onSelectedIdsChange([...set]);
  }

  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (!next) setQuery("");
  }

  return (
    <div className="space-y-1.5 px-2">
      <Popover open={open} onOpenChange={handleOpenChange} modal={false}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="h-8 w-full justify-between gap-1.5 px-2 text-left text-xs font-normal"
            aria-label="选择操作员"
          >
            <span className="truncate text-muted-foreground">
              {selectedIds.length > 0
                ? `已选 ${selectedIds.length} 人`
                : "搜索并选择操作员"}
            </span>
            <ChevronDown className="size-3.5 shrink-0 opacity-60" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] min-w-56 p-0"
          align="start"
          sideOffset={4}
          onOpenAutoFocus={(event) => {
            // 阻止外层过滤 Popover FocusScope 抢焦点；手动聚焦搜索框
            event.preventDefault();
            requestAnimationFrame(() => {
              searchInputRef.current?.focus();
            });
          }}
        >
          <Command shouldFilter={false}>
            <CommandInput
              ref={searchInputRef}
              placeholder="搜索成员…"
              value={query}
              onValueChange={setQuery}
              aria-label="搜索操作员"
            />
            <CommandList className="max-h-48">
              {loading ? (
                <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                  加载中…
                </div>
              ) : loadError ? (
                <div className="px-3 py-6 text-center text-xs text-destructive">{loadError}</div>
              ) : (
                <>
                  <CommandEmpty>无匹配成员</CommandEmpty>
                  <CommandGroup>
                    {filtered.map((op) => {
                      const checked = selectedIds.includes(op.id);
                      return (
                        <CommandItem
                          key={op.id}
                          value={`${op.name} ${op.id}`}
                          data-checked={checked || undefined}
                          onSelect={() => toggle(op.id)}
                        >
                          <span
                            className={cn(
                              "flex size-4 shrink-0 items-center justify-center rounded border border-border",
                              checked &&
                                "border-primary bg-primary text-primary-foreground",
                            )}
                            aria-hidden
                          >
                            {checked ? <Check className="size-3" /> : null}
                          </span>
                          <span className="truncate">{op.name}</span>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {selectedOptions.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {selectedOptions.map((op) => (
            <span
              key={op.id}
              className="inline-flex max-w-full items-center gap-0.5 rounded-md border border-border bg-muted/50 px-1.5 py-0.5 text-[11px]"
            >
              <span className="truncate">{op.name}</span>
              <button
                type="button"
                className="inline-flex size-3.5 shrink-0 items-center justify-center rounded-sm text-muted-foreground hover:text-foreground"
                aria-label={`移除操作员 ${op.name}`}
                onClick={() => toggle(op.id)}
              >
                <X className="size-2.5" />
              </button>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function SizeRangeFields({
  value,
  onChange,
}: {
  value: SizeRangeFilter;
  onChange: (next: SizeRangeFilter) => void;
}) {
  return (
    <div className="space-y-1.5 px-2">
      <SectionTitle>文件大小</SectionTitle>
      <div className="flex flex-wrap items-center gap-1.5">
        <Input
          type="number"
          min={0}
          step="any"
          placeholder="最小"
          value={value.min}
          onChange={(e) => onChange(constrainSizeRangeBound(value, "min", e.target.value))}
          className="h-8 w-20"
          aria-label="最小文件大小"
        />
        <span className="text-muted-foreground">~</span>
        <Input
          type="number"
          min={0}
          step="any"
          placeholder="最大"
          value={value.max}
          onChange={(e) => onChange(constrainSizeRangeBound(value, "max", e.target.value))}
          className="h-8 w-20"
          aria-label="最大文件大小"
        />
        <select
          value={value.unit}
          onChange={(e) => onChange({ ...value, unit: e.target.value as SizeUnit })}
          className="h-8 rounded-md border border-input bg-background px-2 text-sm"
          aria-label="大小单位"
        >
          <option value="KB">KB</option>
          <option value="MB">MB</option>
        </select>
      </div>
    </div>
  );
}

export function MaterialsFilterPopover({
  open,
  onOpenChange,
  accountId,
  operators,
  onOperatorsResolved,
  clientFilters,
  onClientFiltersChange,
  activeCount,
  toolbarRef,
}: Props) {
  const [panelWidth, setPanelWidth] = useState<number | undefined>();

  useEffect(() => {
    const el = toolbarRef.current;
    if (!el) return;

    const measure = () => {
      const w = el.getBoundingClientRect().width;
      if (w > 0) setPanelWidth(w);
    };
    measure();

    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [toolbarRef, open]);

  function patchClient(partial: Partial<MaterialsClientFilters>) {
    onClientFiltersChange({ ...clientFilters, ...partial });
  }

  function toggleFileType(facet: FileTypeFacet | "all") {
    patchClient({ fileTypes: toggleFileTypeFilter(clientFilters.fileTypes, facet) });
  }

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8 gap-1.5"
          aria-label="过滤"
        >
          <ListFilter className="size-3.5" />
          过滤
          {activeCount > 0 ? (
            <span className="rounded-full bg-primary/10 px-1.5 text-[11px] font-medium text-primary">
              {activeCount}
            </span>
          ) : null}
          <ChevronDown className="size-3.5 opacity-60" />
        </Button>
      </PopoverTrigger>
      {/* 以整行工具栏为定位锚点，宽度与工具栏同步 */}
      <PopoverAnchor virtualRef={toolbarRef} />
      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={6}
        style={panelWidth ? { width: panelWidth } : undefined}
        className={cn(
          "flex w-auto max-h-[min(70vh,var(--radix-popover-content-available-height))] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-lg p-0",
        )}
      >
        <div
          className={cn(
            "min-h-0 flex-1 grid gap-4 overflow-y-auto overscroll-contain p-4 sm:grid-cols-2",
          )}
        >
          <div className="min-w-0 space-y-3">
            <div>
              <SectionTitle>文件类型</SectionTitle>
              <FacetCheck
                checked={isFileTypeSelected(clientFilters.fileTypes, "all")}
                indeterminate={isFileTypesSelectAllIndeterminate(clientFilters.fileTypes)}
                label="全部"
                onToggle={() => toggleFileType("all")}
              />
              {FILE_TYPE_OPTIONS.map((opt) => (
                <FacetCheck
                  key={opt.id}
                  checked={isFileTypeSelected(clientFilters.fileTypes, opt.id)}
                  label={opt.label}
                  onToggle={() => toggleFileType(opt.id)}
                />
              ))}
            </div>
          </div>

          <div className="min-w-0 space-y-3">
            <div>
              <SectionTitle>操作员</SectionTitle>
              <OperatorMultiSelect
                accountId={accountId}
                knownOperators={operators}
                selectedIds={clientFilters.operatorIds}
                onSelectedIdsChange={(operatorIds) => patchClient({ operatorIds })}
                onOperatorsResolved={onOperatorsResolved}
              />
            </div>
            <DateRangeFields
              label="上传时间"
              value={clientFilters.createdRange}
              onChange={(createdRange) => patchClient({ createdRange })}
            />
            <DateRangeFields
              label="更新时间"
              value={clientFilters.updatedRange}
              onChange={(updatedRange) => patchClient({ updatedRange })}
            />
            <SizeRangeFields
              value={clientFilters.sizeRange}
              onChange={(sizeRange) => patchClient({ sizeRange })}
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
